import ClearIcon from '@mui/icons-material/Clear';
import { Box, Chip, SelectChangeEvent } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import useStrategicImperativeList from './StrategicImperativeList.hook';
import {
  filtersFormStyle,
  filtersHeaderStyle,
  filtersResultsStyle, filtersResultsValueStyle,
  filtersSectionStyle,
  filtersTitleStyle, resetButtonStyle, selectedTagsStyle, sortSelectStyle, sortSelectWrapperStyle,
} from './StrategicImperativeList.style';
import { AlertMessageType } from 'basics/enums/maya.enums';
import { SelectOption } from 'basics/options/options.types';
import AlertBanner from 'components/AlertBanner/AlertBanner';
import { FormLayoutContainer } from 'components/FormLayout';
import GridList from 'components/GridList/GridList';
import ClearAllIcon from 'components/Icons/ClearAllIcon';
import MayaLayout from 'components/MayaLayout';
import Select from 'yoda-ui/components/Form/Select';
import MultiSelectYoda from 'yoda-ui/components/Form/SelectYoda/MultiSelectYoda';
import { TagStyle } from 'yoda-ui/components/Form/SelectYoda/MultiSelectYoda/MultiSelectYoda.styles';
import TextYoda from 'yoda-ui/components/Form/TextYoda';
import MenuItem from 'yoda-ui/components/MenuItem';
import { YodaFormProvider } from 'yoda-ui/yodaForm';
import { YodaFontSize } from 'yoda-ui/yodaTheme';

const StrategicImperativeList: FC = () => {
  const { t } = useTranslation();
  const flags = useFlags();
  const isSiFilterByTherapeuticArea = flags.mayaAkma419EnableSiCiCepListAdditionalFilters;
  const {
    debouncedHandleChangeSiTitle,
    handleChangeSiTherapeuticArea,
    handleClick,
    loading,
    strategicImperativeListTitle,
    newElementButton,
    onPageChange,
    onPageSizeChange,
    rowCountState,
    siTitleFieldConfig,
    siTherapeuticAreaFieldConfig,
    strategicImperativeListColumnConfig,
    providerFields,
    strategicImperatives,
    selectedFilters,
    handleRemoveChip,
    handleReset,
    hasActiveFilters,
    siSortFieldConfig,
    handleSortChange,
    hasSelectedFilters,
    siSortFilter,
  } = useStrategicImperativeList();

  return (
    <MayaLayout>
      <AlertBanner type={ AlertMessageType.VeevaFatalErrorAlert } />
      <GridList
        getRowId={ (row) => row._id }
        loading={ loading }
        onCellClick={ handleClick }
        title={ strategicImperativeListTitle }
        columnConfig={ strategicImperativeListColumnConfig }
        rows={ strategicImperatives }
        onPageChange={ onPageChange }
        onPageSizeChange={ onPageSizeChange }
        newElementButton={
          flags.mayaBiogenlinc4493EnableStrategicImperativeManagement
            ? newElementButton
            : undefined
        }
        rowCountState={ rowCountState }
        disableColumnMenu={ true }
        customFilters={
          (
            <YodaFormProvider { ...providerFields }>
              <Box sx={ filtersSectionStyle }>
                <Box sx={ filtersHeaderStyle }>
                  <Box sx={ filtersTitleStyle }>{ t('maya_si_filter_title') }</Box>
                  {
                    !loading && (
                      <Box sx={ filtersResultsStyle }>
                        { t('maya_si_filter_results_title') }<Box
                          component="span"
                          sx={ filtersResultsValueStyle }>
                          { t('maya_si_filter_results_value', { shownItems: strategicImperatives.length, allItems: rowCountState }) }
                        </Box>
                      </Box>
                    )
                  }
                </Box>

                <FormLayoutContainer sx={ filtersFormStyle }>
                  <TextYoda
                    onChange={ debouncedHandleChangeSiTitle }
                    { ...siTitleFieldConfig } />
                  {
                    isSiFilterByTherapeuticArea && <MultiSelectYoda
                      onChange={ handleChangeSiTherapeuticArea }
                      { ...siTherapeuticAreaFieldConfig } />
                  }
                </FormLayoutContainer>

                {
                  hasActiveFilters
                    && <Box sx={ selectedTagsStyle }>
                      {
                        selectedFilters.therapeuticArea.map((option) => (
                          <Chip
                            key={ option.id }
                            label={ option.label }
                            sx={ TagStyle }
                            onDelete={ () => handleRemoveChip('therapeuticArea', option.id) }
                            deleteIcon={ <ClearIcon /> }
                          />
                        ))
                      }
                      {
                        hasSelectedFilters && (
                          <Box onClick={ handleReset } sx={ resetButtonStyle }>
                            <ClearAllIcon />
                            { t('actions_reset_label') }
                          </Box>
                        )
                      }
                    </Box>
                }
              </Box>
              <Box sx={ sortSelectWrapperStyle }>
                <Select
                  value={ siSortFilter }
                  onChange={
                    (event: SelectChangeEvent<unknown>, _child: ReactNode) => {
                      const selectedOption = siSortFieldConfig.options.find((opt) => opt.id === event.target.value);
                      if (selectedOption) {
                        handleSortChange(selectedOption);
                      }
                    }
                  }
                  sx={ sortSelectStyle }
                >
                  {
                    siSortFieldConfig.options.map(
                      (option: SelectOption) => {
                        return <MenuItem key={ option.id }
                          value={ option.id } sx={ { fontSize: YodaFontSize.small } }>{ option.label }</MenuItem>;
                      },
                    )
                  }
                </Select>
              </Box>
            </YodaFormProvider>
          )
        }
      />
    </MayaLayout>
  );
};

export default StrategicImperativeList;
